import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useSeasonalTheme } from '../contexts/SeasonalThemeContext';
import LanguageSwitch from './LanguageSwitch';
import { supabase } from '../supabaseClient';
import {
    FaChartPie,
    FaUserInjured,
    FaTooth,
    FaFileInvoiceDollar,
    FaCog,
    FaClipboardList,
    FaSignOutAlt,
    FaUserCircle,
    FaTimes
} from 'react-icons/fa';
import '../styles/Sidebar.css';

const Sidebar = ({ isOpen, onClose }) => {
    const { signOut, profile, isAdmin, isProducer, user } = useAuth();
    const { creditNotes } = useData();
    const { t } = useLanguage();
    const { isSeasonalActive, currentThemeId, themeMeta } = useSeasonalTheme();
    const isHalloween = currentThemeId === 'halloween';
    const isBlackFriday = currentThemeId === 'blackfriday';
    const [unreadCount, setUnreadCount] = useState(0);

    // Count pending credit note requests
    const pendingCreditNotes = (creditNotes || []).filter(n => n.status === 'pending');
    const pendingCount = pendingCreditNotes.length;

    // Fetch unread message count (per-user tracking)
    useEffect(() => {
        if (!profile || !user?.id) return;

        const fetchUnreadCount = async () => {
            try {
                // Get all active case IDs (safely paginated)
                let activeCases = [];
                let cFrom = 0;
                const cBatch = 1000;
                while (true) {
                    const { data, error } = await supabase
                        .from('cases')
                        .select('id')
                        .range(cFrom, cFrom + cBatch - 1);
                    if (error || !data || data.length === 0) break;
                    activeCases.push(...data);
                    if (data.length < cBatch) break;
                    cFrom += cBatch;
                }
                if (!activeCases.length) return;
                const activeCaseIds = new Set(activeCases.map(c => c.id));

                // Get messages sent by the other party (safely paginated)
                const targetSender = isAdmin ? 'partner' : 'lab';
                let allMessages = [];
                let mFrom = 0;
                const mBatch = 1000;
                while (true) {
                    const { data, error } = await supabase
                        .from('case_messages')
                        .select('id, order_id')
                        .eq('sender', targetSender)
                        .range(mFrom, mFrom + mBatch - 1);
                    if (error || !data || data.length === 0) break;
                    allMessages.push(...data);
                    if (data.length < mBatch) break;
                    mFrom += mBatch;
                }

                // Get messages already read by current user (safely paginated)
                let readMessages = [];
                let rFrom = 0;
                const rBatch = 1000;
                while (true) {
                    const { data, error } = await supabase
                        .from('message_reads')
                        .select('message_id')
                        .eq('user_id', user.id)
                        .range(rFrom, rFrom + rBatch - 1);
                    if (error || !data || data.length === 0) break;
                    readMessages.push(...data);
                    if (data.length < rBatch) break;
                    rFrom += rBatch;
                }

                const readIds = new Set(readMessages.map(r => r.message_id));
                const unread = allMessages.filter(m => 
                    activeCaseIds.has(m.order_id) && !readIds.has(m.id)
                ).length;
                setUnreadCount(unread);
            } catch (err) {
                console.error('Error fetching unread count:', err);
            }
        };

        fetchUnreadCount();

        // Immediate update on message read or marked unread
        const handleEvent = () => fetchUnreadCount();
        window.addEventListener('messages-read', handleEvent);
        window.addEventListener('messages-unread', handleEvent);

        // Poll every 30 seconds for new messages
        const interval = setInterval(fetchUnreadCount, 30000);
        return () => {
            clearInterval(interval);
            window.removeEventListener('messages-read', handleEvent);
            window.removeEventListener('messages-unread', handleEvent);
        };
    }, [profile, user, isAdmin]);

    // Build menu items based on role
    const getMenuItems = () => {
        if (isProducer) {
            // Producteur: only Travaux + Mon Profil
            return [
                { path: '/jobs', name: t('nav.jobs', 'Travaux'), icon: isHalloween ? '💀' : <FaTooth />, badge: unreadCount },
                { path: '/profile', name: t('nav.profile', 'Mon Profil'), icon: isHalloween ? '👻' : <FaUserCircle /> },
            ];
        }

        // Admin: full menu
        return [
            { path: '/dashboard', name: t('nav.dashboard', 'Tableau de bord'), icon: isHalloween ? '🦇' : <FaChartPie /> },
            { path: '/jobs', name: t('nav.jobs', 'Travaux'), icon: isHalloween ? '💀' : <FaTooth />, badge: unreadCount },
            { path: '/products', name: t('nav.products', 'Produits'), icon: isHalloween ? '🧪' : <FaClipboardList /> },
            { path: '/clients', name: t('nav.clients', 'Praticiens'), icon: isHalloween ? '😈' : <FaUserInjured /> },
            { path: '/accounting', name: t('nav.accounting', 'Comptabilité'), icon: isHalloween ? '🍲' : <FaFileInvoiceDollar />, badge: pendingCount },
            { path: '/profile', name: t('nav.profile', 'Mon Profil'), icon: isHalloween ? '👻' : <FaUserCircle /> },
            { path: '/settings', name: t('nav.settings', 'Paramètres'), icon: isHalloween ? '🪦' : <FaCog /> },
        ];
    };

    const menuItems = getMenuItems();

    const handleLinkClick = () => {
        if (onClose) onClose();
    };

    const handleSignOut = () => {
        if (onClose) onClose();
        signOut();
    };

    return (
        <div className={`sidebar ${isOpen ? 'open' : ''}`}>
            <div className="sidebar-header d-flex justify-content-between align-items-center">
                <div className="text-start">
                    <h3 className="d-flex align-items-center gap-2">
                        ENOVADENT
                        {isSeasonalActive && !isBlackFriday && (
                            <span className="seasonal-banner-badge" title={themeMeta.name}>
                                {themeMeta.icon}
                            </span>
                        )}
                    </h3>
                    <p>{isProducer ? t('nav.partner', 'Partenaire') : t('nav.lab_manager', 'Lab Manager')}</p>
                </div>
                {isBlackFriday && (
                    <span className="bf-header-square-badge d-none d-md-inline-flex" title={themeMeta.name}>
                        ✖
                    </span>
                )}
                {/* Close Button for Mobile view */}
                <button className="sidebar-close-btn d-md-none" onClick={onClose} aria-label="Fermer le menu">
                    <FaTimes />
                </button>
            </div>

            {/* Language Switcher inside Sidebar */}
            <div style={{ padding: '0 18px 12px 18px' }}>
                <LanguageSwitch />
            </div>
            
            <nav className="sidebar-nav">
                {menuItems.map((item) => (
                    <NavLink
                        key={item.path}
                        to={item.path}
                        className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
                        onClick={handleLinkClick}
                    >
                        <span className="sidebar-icon">{item.icon}</span>
                        <span className="sidebar-text">{item.name}</span>
                        {item.badge > 0 && (
                            <span className="sidebar-badge">{item.badge}</span>
                        )}
                    </NavLink>
                ))}
            </nav>

            <div className="sidebar-footer" style={{ paddingBottom: '20px' }}>
                <button
                    onClick={handleSignOut}
                    className="sidebar-link"
                    style={{ border: 'none', background: 'transparent', width: '100%', cursor: 'pointer', color: '#ff6b6b' }}
                >
                    <span className="sidebar-icon">{isHalloween ? '🚪' : <FaSignOutAlt />}</span>
                    <span className="sidebar-text">{t('nav.logout', 'Déconnexion')}</span>
                </button>
            </div>
        </div>
    );
};

export default Sidebar;

